<template>
  <div>
    <!-- Filtros y resumen del período -->
    <b-card no-body>
      <b-card-body>
        <p class="mb-1">
          <feather-icon
            icon="InfoIcon"
            size="18"
            class="mr-50"
          />
          Venta caída: unidades solicitadas en una cotización que no se pudieron atender por falta de stock al generar el pedido.
          Se registra automáticamente al convertir la cotización.
        </p>

        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Desde"
              class="mb-1 mb-md-0"
            >
              <b-form-input
                v-model="startDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Hasta"
              class="mb-1 mb-md-0"
            >
              <b-form-input
                v-model="endDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="isAdmin"
            cols="12"
            md="3"
          >
            <b-form-group
              label="Vendedor"
              class="mb-1 mb-md-0"
            >
              <v-select
                v-model="selectedVendor"
                :options="vendorOptions"
                label="label"
                placeholder="Todos"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            :md="isAdmin ? 3 : 6"
          >
            <b-form-group
              label="Buscar"
              class="mb-0"
            >
              <b-form-input
                v-model="searchQuery"
                placeholder="Cliente, SKU, producto, # cotización o # pedido"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div
          v-if="summary"
          class="lost-sales-summary mt-2"
        >
          <div class="lost-sales-summary__tile lost-sales-summary__tile--danger">
            <div class="lost-sales-summary__value">
              {{ summary.caido }}
            </div>
            <div class="lost-sales-summary__label">
              Unidades caídas
            </div>
          </div>
          <div class="lost-sales-summary__tile">
            <div class="lost-sales-summary__value">
              {{ summary.solicitado }}
            </div>
            <div class="lost-sales-summary__label">
              Solicitadas (líneas con faltante)
            </div>
          </div>
          <div class="lost-sales-summary__tile lost-sales-summary__tile--success">
            <div class="lost-sales-summary__value">
              {{ summary.atendido }}
            </div>
            <div class="lost-sales-summary__label">
              Atendidas (líneas con faltante)
            </div>
          </div>
          <div class="lost-sales-summary__tile lost-sales-summary__tile--warning">
            <div class="lost-sales-summary__value">
              {{ summary.porcentaje_sobre_cotizado }}%
            </div>
            <div class="lost-sales-summary__label">
              Sobre lo cotizado ({{ summary.cotizado_en_periodo }} u.)
            </div>
          </div>
          <div class="lost-sales-summary__tile">
            <div class="lost-sales-summary__value">
              {{ summary.cotizaciones }}
            </div>
            <div class="lost-sales-summary__label">
              Cotizaciones afectadas
            </div>
          </div>
          <div
            v-for="m in summary.monto_por_moneda || []"
            :key="m.code"
            class="lost-sales-summary__tile lost-sales-summary__tile--dark"
          >
            <div class="lost-sales-summary__value">
              {{ m.symbol }}{{ formatAmount(m.monto) }}
            </div>
            <div class="lost-sales-summary__label">
              Monto caído {{ m.code }}
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Detalle -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Mostrar</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
            <download-excel
              v-if="rows.length"
              class="btn btn-outline-secondary btn-sm"
              :data="rows"
              :fields="excelFields"
              worksheet="Venta caída"
              :name="'venta_caida_' + getCurrentDate() + '.xls'"
            >
              <feather-icon icon="DownloadIcon" />
              Descargar Excel
            </download-excel>
          </b-col>
          <b-col
            v-if="meta"
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted">Mostrando del {{ meta.from || 0 }} al {{ meta.to || 0 }} de {{ meta.total }} registros</span>
          </b-col>
        </b-row>
      </div>

      <b-table
        class="position-relative"
        hover
        responsive
        small
        :busy="tableIsBusy"
        primary-key="Id"
        show-empty
        empty-text="No hay venta caída registrada en el período"
        :items="rows"
        :fields="fields"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <template
          v-for="col in sortableColumns"
          #[`head(${col})`]="data"
        >
          <span
            :key="col"
            class="text-dark pointer"
            @click="sortColumn(col)"
          >{{ data.label }}
            <feather-icon
              v-if="sortKey === col"
              :icon="sortDesc === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="12"
            />
          </span>
        </template>

        <template #cell(Cotizacion)="data">
          <b-link
            class="font-weight-bold text-dark"
            @click="$router.push({ name: 'sales-customer-quotes', params: { id: data.item.Cotizacion } })"
          >
            #{{ data.item.Cotizacion }}
          </b-link>
          <small class="text-muted d-block">{{ data.item.EstadoCotizacion }}</small>
        </template>

        <template #cell(Pedido)="data">
          <span v-if="data.item.Pedido">
            <b-link
              class="font-weight-bold text-primary"
              @click="$router.push({ name: 'sales-customer-orders', params: { id: data.item.Pedido } })"
            >
              #{{ data.item.Pedido }}
            </b-link>
            <b-badge
              :variant="orderStatusVariant(data.item.EstadoPedido)"
              class="d-block mt-25"
            >
              {{ data.item.EstadoPedido }}
            </b-badge>
          </span>
          <span
            v-else
            class="text-muted"
          >
            Sin pedido
          </span>
        </template>

        <template #cell(Sku)="data">
          <div class="font-weight-bold">
            {{ data.item.Sku }}
          </div>
          <small class="text-muted">{{ data.item.Descripcion }} · {{ data.item.Color }} · Talla {{ data.item.Talla }}</small>
        </template>

        <template #cell(Caida)="data">
          <b-badge variant="light-danger">
            {{ data.item.Caida }}
          </b-badge>
        </template>

        <template #cell(MontoCaido)="data">
          {{ data.item.MonedaSimbolo }}.{{ data.item.MontoCaido }}
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-if="meta"
              v-model="currentPage"
              :total-rows="meta.total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import downloadExcel from 'vue-json-excel'

function isoDate(date) {
  return date.toISOString().slice(0, 10)
}

export default {
  components: { vSelect, downloadExcel },

  data() {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)

    return {
      startDate: isoDate(firstDay),
      endDate: isoDate(today),
      selectedVendor: null,
      vendorOptions: [],
      searchQuery: '',
      searchQueryTimeout: null,

      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      sortKey: 'Fecha',
      sortDesc: 'desc',

      rows: [],
      meta: null,
      summary: null,
      tableIsBusy: true,

      sortableColumns: ['Fecha', 'Cotizacion', 'Pedido', 'Solicitado', 'Atendido', 'Caida'],
      sortFieldMap: {
        Fecha: 'created_at',
        Cotizacion: 'customer_quote_id',
        Pedido: 'customer_order_id',
        Solicitado: 'requested_quantity',
        Atendido: 'fulfilled_quantity',
        Caida: 'lost_quantity',
      },

    }
  },

  computed: {
    isAdmin() {
      const user = JSON.parse(localStorage.getItem('DuttonAuthUser') || '{}')
      return user.role === 'admin'
    },

    // Los montos son solo para administración; el vendedor ve unidades
    fields() {
      const base = [
        { key: 'Fecha', label: 'Fecha' },
        { key: 'Cotizacion', label: 'Cotización' },
        { key: 'Pedido', label: 'Pedido' },
        { key: 'Cliente', label: 'Cliente' },
        { key: 'Vendedor', label: 'Vendedor' },
        { key: 'Sku', label: 'Producto' },
        { key: 'Solicitado', label: 'Solicitado', class: 'text-center' },
        { key: 'Atendido', label: 'Atendido', class: 'text-center' },
        { key: 'Caida', label: 'Venta caída', class: 'text-center' },
        { key: 'DisponibleAlConvertir', label: 'Disponible al convertir', class: 'text-center' },
      ]
      if (this.isAdmin) {
        base.push({ key: 'PrecioUnitario', label: 'Precio', class: 'text-right' })
        base.push({ key: 'MontoCaido', label: 'Monto caído', class: 'text-right' })
      }
      return base
    },

    excelFields() {
      const base = {
        Fecha: 'Fecha',
        Cotización: 'Cotizacion',
        'Estado cotización': 'EstadoCotizacion',
        Pedido: 'Pedido',
        'Estado pedido': 'EstadoPedido',
        Cliente: 'Cliente',
        Vendedor: 'Vendedor',
        SKU: 'Sku',
        Producto: 'Producto',
        Descripción: 'Descripcion',
        Color: 'Color',
        Talla: 'Talla',
        Solicitado: 'Solicitado',
        Atendido: 'Atendido',
        'Venta caída': 'Caida',
        'Disponible al convertir': 'DisponibleAlConvertir',
      }
      if (this.isAdmin) {
        base['Precio unitario'] = 'PrecioUnitario'
        base['Monto caído'] = 'MontoCaido'
        base.Moneda = 'Moneda'
      }
      return base
    },

    requestParams() {
      const params = {
        start: this.startDate || undefined,
        end: this.endDate || undefined,
      }
      if (this.selectedVendor) params.user_id = this.selectedVendor.id
      return params
    },
  },

  watch: {
    perPage() {
      this.currentPage = 1
      this.fetchData()
    },
    currentPage() {
      this.fetchData()
    },
    startDate() {
      this.currentPage = 1
      this.fetchAll()
    },
    endDate() {
      this.currentPage = 1
      this.fetchAll()
    },
    selectedVendor() {
      this.currentPage = 1
      this.fetchAll()
    },
    searchQuery() {
      clearTimeout(this.searchQueryTimeout)
      this.searchQueryTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchData()
      }, 300)
    },
  },

  created() {
    if (this.isAdmin) this.getVendors()
    this.fetchAll()
  },

  methods: {
    fetchAll() {
      this.fetchSummary()
      this.fetchData()
    },

    fetchSummary() {
      axios
        .get('dashboard/lost-sales-summary', { params: this.requestParams })
        .then(response => { this.summary = response.data })
        .catch(error => { this.showErrors(error) })
    },

    fetchData() {
      this.tableIsBusy = true
      axios
        .get('lost-sale', {
          params: {
            ...this.requestParams,
            perPage: this.perPage,
            page: this.currentPage,
            query: this.searchQuery,
            sortField: this.sortFieldMap[this.sortKey] || 'created_at',
            sortDesc: this.sortDesc,
          },
        })
        .then(response => {
          this.rows = response.data.data || []
          this.meta = response.data.meta || null
        })
        .catch(error => { this.showErrors(error) })
        .finally(() => { this.tableIsBusy = false })
    },

    getVendors() {
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}`)
        .then(response => {
          this.vendorOptions = response.data.data.map(el => ({ label: el.name, id: el.id }))
        })
        .catch(error => { this.showErrors(error) })
    },

    sortColumn(column) {
      if (this.sortKey === column) {
        this.sortDesc = this.sortDesc === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = column
        this.sortDesc = 'desc'
      }
      this.fetchData()
    },

    orderStatusVariant(status) {
      switch (status) {
        case 'Pendiente': return 'light-primary'
        case 'Autorizado': return 'light-success'
        case 'Despachado': return 'light-info'
        case 'Facturado': return 'light-secondary'
        case 'Correcciones': return 'light-warning'
        case 'Rechazado':
        case 'Anulado': return 'light-danger'
        default: return 'light-secondary'
      }
    },

    formatAmount(value) {
      return Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.pointer {
  cursor: pointer;
}
.lost-sales-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;

  &__tile {
    min-width: 150px;
    padding: 0.7rem 1rem;
    border: 2px solid #d8dee9;
    border-radius: 8px;
    background: #fafbfc;

    &--danger { border-color: #ea5455; }
    &--success { border-color: #28c76f; }
    &--warning { border-color: #ff9f43; }
    &--dark { border-color: #4b4b4b; }
  }

  &__value {
    font-size: 1.6rem;
    font-weight: 700;
    color: #222;
    line-height: 1.2;
  }

  &__label {
    font-size: 0.85rem;
    color: #666;
    margin-top: 0.15rem;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
